import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { LOCAL_STORAGE } from './user-storage.service';

/**
 * Default expiry duration in minutes for artwork environment override.
 * This is the initial value shown in the settings UI and used if user doesn't change it.
 *
 * To change the default: Update this constant.
 * Valid range: 15-120 minutes
 */
const DEFAULT_OVERRIDE_EXPIRY_MINUTES = 15;

/**
 * Minimum allowed expiry duration in minutes
 */
const MIN_EXPIRY_MINUTES = 15;

/**
 * Maximum allowed expiry duration in minutes
 */
const MAX_EXPIRY_MINUTES = 120;

interface ArtworkOverride {
  environment: string;
  expiryMinutes: number;
  expiresAt: string; // ISO timestamp
}

interface OverrideInfo {
  environment: string;
  expiresAt: Date;
}

/**
 * Service for managing environment-specific features and artwork override.
 *
 * This service handles temporary overriding of the artwork environment for demo purposes.
 * The override persists in localStorage and automatically expires after a user-configured duration.
 */
@Injectable({ providedIn: 'root' })
export class EnvironmentService implements OnDestroy {
  private readonly OVERRIDE_KEY = 'artwork_override';
  private readonly localStorage = inject(LOCAL_STORAGE);
  private checkExpiryInterval?: number;

  // Signal to track when override changes (triggers reactivity)
  private overrideVersion = signal(0);

  // Computed signal for artwork path (reactive to override changes)
  readonly artworkPath = computed(() => {
    // Subscribe to override version changes
    this.overrideVersion();

    const override = this.getActiveOverride();
    if (override) {
      return `artwork/${override.environment}`;
    }
    return environment.artworkPath;
  });

  /**
   * Artwork path for brand logos (header, landing, login, etc.): always production
   * art, unless a demo override is active. Environment-specific art is reserved
   * for the nav menu.
   */
  readonly brandArtworkPath = computed(() => {
    this.overrideVersion();
    const override = this.getActiveOverride();
    return override ? `artwork/${override.environment}` : 'artwork/production';
  });

  constructor() {
    this.initializeExpiryCheck();
  }

  /**
   * Get the default expiry duration in minutes
   */
  getDefaultExpiryMinutes(): number {
    return DEFAULT_OVERRIDE_EXPIRY_MINUTES;
  }

  /**
   * Get the minimum allowed expiry duration in minutes
   */
  getMinExpiryMinutes(): number {
    return MIN_EXPIRY_MINUTES;
  }

  /**
   * Get the maximum allowed expiry duration in minutes
   */
  getMaxExpiryMinutes(): number {
    return MAX_EXPIRY_MINUTES;
  }

  /**
   * Get the current artwork path prefix with override if active.
   * Returns the full path prefix (e.g., 'artwork/production') for constructing asset URLs.
   *
   * @deprecated Use the artworkPath signal directly for reactivity
   */
  getArtworkPath(): string {
    return this.artworkPath();
  }

  /**
   * Get the current environment name (normalized to lowercase with hyphens)
   */
  getCurrentEnvironment(): string {
    // Normalize environment name to match option values
    // "Production" -> "production", "Integration Local" -> "integration-local"
    return environment.name.toLowerCase().replace(/\s+/g, '-');
  }

  /**
   * Get all available environment names for the override dropdown
   */
  getAvailableEnvironments(): { value: string; label: string }[] {
    return [
      { value: 'development', label: 'Development' },
      { value: 'integration-local', label: 'Integration (Local)' },
      { value: 'integration', label: 'Integration' },
      { value: 'staging-local', label: 'Staging (Local)' },
      { value: 'staging', label: 'Staging' },
      { value: 'production-local', label: 'Production (Local)' },
      { value: 'production', label: 'Production' },
    ];
  }

  /**
   * Set artwork environment override.
   * If environmentName matches current environment, clears any existing override.
   *
   * @param environmentName - Target environment name
   * @param expiryMinutes - Duration before auto-revert (15-120 minutes)
   */
  setArtworkOverride(environmentName: string, expiryMinutes: number): void {
    // Validate expiry range
    const validExpiry = Math.max(MIN_EXPIRY_MINUTES, Math.min(MAX_EXPIRY_MINUTES, expiryMinutes));

    // Setting to current environment clears the override
    if (environmentName === this.getCurrentEnvironment()) {
      this.clearArtworkOverride();
      return;
    }

    const now = new Date();
    const expiresAt = new Date(now.getTime() + validExpiry * 60 * 1000);

    const override: ArtworkOverride = {
      environment: environmentName,
      expiryMinutes: validExpiry,
      expiresAt: expiresAt.toISOString(),
    };

    this.localStorage.setItem(this.OVERRIDE_KEY, JSON.stringify(override));

    // Increment version to trigger reactive updates
    this.overrideVersion.update((v) => v + 1);
  }

  /**
   * Clear any active artwork override
   */
  clearArtworkOverride(): void {
    this.localStorage.removeItem(this.OVERRIDE_KEY);

    // Increment version to trigger reactive updates
    this.overrideVersion.update((v) => v + 1);
  }

  /**
   * Get current override details if active and not expired.
   * Returns null if no override or expired.
   */
  getCurrentOverride(): OverrideInfo | null {
    const override = this.getActiveOverride();
    if (!override) return null;

    return {
      environment: override.environment,
      expiresAt: new Date(override.expiresAt),
    };
  }

  /**
   * Get active override if valid and not expired.
   * Automatically clears expired overrides.
   */
  private getActiveOverride(): ArtworkOverride | null {
    const stored = this.localStorage.getItem(this.OVERRIDE_KEY);
    if (!stored) return null;

    try {
      const override: ArtworkOverride = JSON.parse(stored);
      const now = new Date();
      const expiresAt = new Date(override.expiresAt);

      // Check if expired
      if (now >= expiresAt) {
        // Clear without triggering version update (will be triggered by periodic check)
        this.localStorage.removeItem(this.OVERRIDE_KEY);
        this.overrideVersion.update((v) => v + 1);
        return null;
      }

      return override;
    } catch (error) {
      // Invalid data, clear it
      this.clearArtworkOverride();
      return null;
    }
  }

  /**
   * Initialize periodic expiry checking.
   * Checks every minute and auto-clears expired overrides.
   */
  private initializeExpiryCheck(): void {
    // Check every minute
    this.checkExpiryInterval = window.setInterval(() => {
      this.getActiveOverride(); // Will auto-clear if expired
    }, 60000);
  }

  ngOnDestroy(): void {
    if (this.checkExpiryInterval) {
      clearInterval(this.checkExpiryInterval);
    }
  }
}
